import { useCallback, useEffect, useState } from "react";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

// Tracks Android/Chromium PWA installability. The `beforeinstallprompt` event is
// captured globally at app startup (see index.js) because Chrome fires it once,
// often before this hook mounts. We read that stashed event and also listen for
// a late firing. `installable` stays true every session until the app is actually
// installed, so the install CTA keeps reappearing on each login until installed.
export function usePwaInstall() {
  const [deferred, setDeferred] = useState(
    typeof window !== "undefined" ? window.__deferredInstallPrompt || null : null
  );
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const sync = () => setDeferred(window.__deferredInstallPrompt || null);
    const onPrompt = (e) => {
      e.preventDefault();
      window.__deferredInstallPrompt = e;
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      window.__deferredInstallPrompt = null;
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("vt_install_prompt_ready", sync);
    window.addEventListener("appinstalled", onInstalled);
    sync();
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("vt_install_prompt_ready", sync);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const evt = deferred || (typeof window !== "undefined" ? window.__deferredInstallPrompt : null);
    if (!evt) return "unavailable";
    evt.prompt();
    const { outcome } = await evt.userChoice;
    window.__deferredInstallPrompt = null;
    setDeferred(null);
    return outcome; // "accepted" | "dismissed"
  }, [deferred]);

  return { installable: !!deferred && !installed, installed, promptInstall };
}
